import React, { useState, useEffect, useCallback } from 'react';
import {
  getVolunteers, createVolunteer, updateVolunteer, updateVolunteerStatus,
  VOLUNTEER_SKILLS, LANGUAGES, SINGAPORE_DISTRICTS,
  fmtDate, getEarnedMilestones,
} from '../../../api/communityApi';
import { getPendingVolunteerRoles, approveVolunteerRole } from '../../../api/programmesApi';

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cls =
    status === 'active'           ? 'bb-com-status-active'   :
    status === 'pending_approval' ? 'bb-com-status-pending'  : 'bb-com-status-inactive';
  const label =
    status === 'active'           ? 'Active'   :
    status === 'pending_approval' ? 'Pending'  : 'Inactive';
  return <span className={cls}>{label}</span>;
}

function SkillChips({ skills = [] }) {
  return (
    <span>
      {skills.map(s => {
        const found = VOLUNTEER_SKILLS.find(v => v.value === s);
        return <span key={s} className="bb-com-skill-chip">{found ? found.label : s}</span>;
      })}
    </span>
  );
}

function MilestoneChips({ volunteer }) {
  const earned = getEarnedMilestones(volunteer);
  if (!earned.length) return <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>;
  return (
    <>
      {earned.map(m => (
        <span key={m.key} className="bb-com-milestone">{m.icon} {m.label}</span>
      ))}
    </>
  );
}

// ── Edit Modal ─────────────────────────────────────────────────────────────

function EditVolunteerModal({ volunteer, orgId, onClose, onSaved }) {
  const [form, setForm] = useState({
    displayName: volunteer.displayName || '',
    email:       volunteer.email || '',
    phone:       volunteer.phone || '',
    skills:      volunteer.skills || [],
    languages:   volunteer.languages || [],
    district:    volunteer.district || '',
    bio:         volunteer.bio || '',
    notes:       volunteer.notes || '',
    status:      volunteer.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const toggle = (field, value) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter(x => x !== value)
        : [...f[field], value],
    }));
  };

  const handleSave = async () => {
    if (!form.displayName.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await updateVolunteer(orgId, volunteer.id, form);
      if (form.status !== volunteer.status) {
        await updateVolunteerStatus(orgId, volunteer.id, form.status);
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-com-modal">
        <h3>Edit Volunteer — {volunteer.displayName}</h3>
        {error && <div className="bb-alert bb-alert--error" style={{ margin: '0 0 12px' }}>{error}</div>}

        <div className="bb-form-group">
          <label>Name <span className="bb-required">*</span></label>
          <input className="bb-input" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>Email <span className="bb-required">*</span></label>
          <input className="bb-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>Phone</label>
          <input className="bb-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>Status</label>
          <select className="bb-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            <option value="active">Active</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="bb-form-group">
          <label>District</label>
          <select className="bb-select" value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))}>
            <option value="">— Select —</option>
            {SINGAPORE_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="bb-form-group">
          <label>Skills</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {VOLUNTEER_SKILLS.map(s => (
              <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <input type="checkbox" checked={form.skills.includes(s.value)} onChange={() => toggle('skills', s.value)} />
                {s.label}
              </label>
            ))}
          </div>
        </div>
        <div className="bb-form-group">
          <label>Languages</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LANGUAGES.map(l => (
              <label key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <input type="checkbox" checked={form.languages.includes(l.value)} onChange={() => toggle('languages', l.value)} />
                {l.label}
              </label>
            ))}
          </div>
        </div>
        <div className="bb-form-group">
          <label>Bio</label>
          <textarea className="bb-textarea" rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>Admin Notes</label>
          <textarea className="bb-textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        <div className="bb-com-modal-footer">
          <button className="bb-btn-outline-sm" onClick={onClose}>Cancel</button>
          <button className="bb-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Engagement History Modal ───────────────────────────────────────────────

function EngagementHistoryModal({ volunteer, onClose }) {
  const milestones = getEarnedMilestones(volunteer);

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontWeight: 600, color: '#374151', fontSize: 13, minWidth: 180 }}>{label}</span>
      <span style={{ color: '#111827', fontSize: 13, textAlign: 'right', flex: 1 }}>{value}</span>
    </div>
  );

  const ListField = ({ items }) => {
    if (!items || items.length === 0) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        {items.map((item, i) => (
          <span key={i} style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{item}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-com-modal" style={{ maxWidth: 540 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Engagement History — {volunteer.displayName}</h3>
          <button className="bb-btn-outline-sm" onClick={onClose}>✕</button>
        </div>

        <Row
          label="Total Hours"
          value={<span style={{ fontWeight: 700, color: '#4338CA' }}>{(volunteer.totalHours || 0).toFixed(1)} h</span>}
        />
        <Row
          label="Milestones Earned"
          value={
            milestones.length
              ? <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {milestones.map(m => <span key={m.key} className="bb-com-milestone">{m.icon} {m.label}</span>)}
                </span>
              : <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>
          }
        />
        <Row
          label="Total Events Attended"
          value={<span style={{ fontWeight: 700 }}>{volunteer.totalEventsAttended ?? '—'}</span>}
        />
        <Row
          label="Events Attended"
          value={<ListField items={volunteer.eventsAttended} />}
        />
        <Row
          label="Total Events Volunteered"
          value={<span style={{ fontWeight: 700 }}>{volunteer.totalEventsVolunteered ?? '—'}</span>}
        />
        <Row
          label="Events Volunteered"
          value={<ListField items={volunteer.eventsVolunteered} />}
        />
        <Row
          label="Total Members Served"
          value={<span style={{ fontWeight: 700 }}>{volunteer.totalMembersServed ?? (volunteer.membersServed ? volunteer.membersServed.length : '—')}</span>}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0' }}>
          <span style={{ fontWeight: 600, color: '#374151', fontSize: 13, minWidth: 180 }}>Members Served</span>
          <ListField items={volunteer.membersServed} />
        </div>

        <div className="bb-com-modal-footer" style={{ marginTop: 8 }}>
          <button className="bb-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Volunteer table ────────────────────────────────────────────────────────

function VolunteerTable({ volunteers, orgId, adminId, onRefresh, showApproveReject = false, showCreateBtn = false }) {
  const [search, setSearch]               = useState('');
  const [editing, setEditing]             = useState(null);
  const [showCreate, setShowCreate]       = useState(false);
  const [engagementView, setEngagementView] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const filtered = volunteers.filter(v => {
    const q = search.toLowerCase();
    return (
      (v.displayName || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q)
    );
  });

  const setStatus = async (v, status) => {
    setActionLoading(s => ({ ...s, [v.id]: true }));
    try {
      await updateVolunteerStatus(orgId, v.id, status);
      onRefresh();
    } finally {
      setActionLoading(s => ({ ...s, [v.id]: false }));
    }
  };

  return (
    <>
      <div className="bb-com-search">
        <input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {showCreateBtn && (
          <button className="bb-btn-primary" style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }} onClick={() => setShowCreate(true)}>
            + Create Volunteer
          </button>
        )}
      </div>

      {editing && (
        <EditVolunteerModal
          volunteer={editing}
          orgId={orgId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}

      {engagementView && (
        <EngagementHistoryModal
          volunteer={engagementView}
          onClose={() => setEngagementView(null)}
        />
      )}

      {showCreate && (
        <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="bb-com-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Create Volunteer</h3>
              <button className="bb-btn-outline-sm" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <CreateVolunteerForm orgId={orgId} adminId={adminId} onCreated={() => { setShowCreate(false); onRefresh(); }} />
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="bb-com-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>District</th>
              <th>Skills</th>
              <th>Current Milestone</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No volunteers found.</td></tr>
            )}
            {filtered.map(v => (
              <tr key={v.id}>
                <td style={{ fontWeight: 600 }}>{v.displayName}</td>
                <td>{v.email}</td>
                <td>{v.phone || '—'}</td>
                <td>{v.district || '—'}</td>
                <td><SkillChips skills={v.skills} /></td>
                <td>{(() => { const ms = getEarnedMilestones(v); const cur = ms[ms.length - 1]; return cur ? `${cur.icon} ${cur.label}` : '—'; })()}</td>
                <td>{fmtDate(v.createdAt)}</td>
                <td>
                  <div className="bb-action-row">
                    {showApproveReject ? (
                      <>
                        <button
                          className="bb-btn-primary"
                          style={{ fontSize: 12, padding: '4px 10px' }}
                          disabled={actionLoading[v.id]}
                          onClick={() => setStatus(v, 'active')}
                        >Approve</button>
                        <button
                          className="bb-btn-danger-sm"
                          disabled={actionLoading[v.id]}
                          onClick={() => setStatus(v, 'inactive')}
                        >Reject</button>
                      </>
                    ) : (
                      <>
                        <button className="bb-btn-outline-sm" onClick={() => setEditing(v)}>Edit</button>
                        <button className="bb-btn-outline-sm" onClick={() => setEngagementView(v)}>View Engagement History</button>
                        {v.status !== 'active' && (
                          <button
                            className="bb-btn-sm"
                            disabled={actionLoading[v.id]}
                            onClick={() => setStatus(v, 'active')}
                          >Activate</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Create Volunteer Form ──────────────────────────────────────────────────

function CreateVolunteerForm({ orgId, adminId, onCreated }) {
  const empty = {
    displayName: '', email: '', phone: '',
    skills: [], languages: [], district: '',
    bio: '', notes: '', adminCreated: true,
  };
  const [form, setForm]     = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  const toggle = (field, value) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter(x => x !== value)
        : [...f[field], value],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.displayName.trim()) e.displayName = 'Name is required.';
    if (!form.email.trim())       e.email = 'Email is required.';
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setApiError('');
    try {
      await createVolunteer(orgId, form, adminId);
      setSuccess('Volunteer created successfully!');
      setForm(empty);
      setErrors({});
      onCreated && onCreated();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setApiError(err.message || 'Failed to create volunteer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {success  && <div className="bb-alert bb-alert--info"   style={{ margin: '0 0 16px' }}>{success}</div>}
      {apiError && <div className="bb-alert bb-alert--error"  style={{ margin: '0 0 16px' }}>{apiError}</div>}

      <div className="bb-form-row">
        <div className="bb-form-group">
          <label>Full Name <span className="bb-required">*</span></label>
          <input
            className={`bb-input${errors.displayName ? ' bb-input--error' : ''}`}
            value={form.displayName}
            onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
          />
          {errors.displayName && <span className="bb-field-error">{errors.displayName}</span>}
        </div>
        <div className="bb-form-group">
          <label>Email <span className="bb-required">*</span></label>
          <input
            className={`bb-input${errors.email ? ' bb-input--error' : ''}`}
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          {errors.email && <span className="bb-field-error">{errors.email}</span>}
        </div>
      </div>

      <div className="bb-form-row">
        <div className="bb-form-group">
          <label>Phone</label>
          <input className="bb-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>District</label>
          <select className="bb-select" value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))}>
            <option value="">— Select district —</option>
            {SINGAPORE_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="bb-form-group">
        <label>Skills</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {VOLUNTEER_SKILLS.map(s => (
            <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.skills.includes(s.value)} onChange={() => toggle('skills', s.value)} />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      <div className="bb-form-group">
        <label>Languages</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {LANGUAGES.map(l => (
            <label key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.languages.includes(l.value)} onChange={() => toggle('languages', l.value)} />
              {l.label}
            </label>
          ))}
        </div>
      </div>

      <div className="bb-form-group">
        <label>Bio</label>
        <textarea className="bb-textarea" rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
      </div>

      <div className="bb-form-group">
        <label>Admin Notes</label>
        <textarea className="bb-textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </div>

      <button type="submit" className="bb-btn-primary" disabled={saving}>
        {saving ? 'Creating…' : 'Create Volunteer'}
      </button>
    </form>
  );
}

// ── Volunteer Engagement Dashboard Tab ────────────────────────────────────

function VolunteerEngagementDashboardTab({ volunteers }) {
  const buckets = {
    high:   volunteers.filter(v => (v.totalHours || 0) >= 50),
    medium: volunteers.filter(v => (v.totalHours || 0) >= 10 && (v.totalHours || 0) < 50),
    low:    volunteers.filter(v => (v.totalHours || 0) < 10),
  };

  const sorted = [...volunteers].sort((a, b) => (b.totalHours || 0) - (a.totalHours || 0));

  const bucketConfig = [
    { key: 'high',   label: '50+ Hours',   color: '#065F46', bg: '#D1FAE5' },
    { key: 'medium', label: '10–49 Hours',  color: '#1E40AF', bg: '#DBEAFE' },
    { key: 'low',    label: '<10 Hours',    color: '#92400E', bg: '#FEF3C7' },
  ];

  return (
    <div>
      <div className="bb-com-stats">
        {bucketConfig.map(t => (
          <div key={t.key} className="bb-com-stat-card" style={{ borderTop: `3px solid ${t.color}` }}>
            <div className="bb-com-stat-val" style={{ color: t.color }}>{buckets[t.key].length}</div>
            <div className="bb-com-stat-lbl">{t.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {bucketConfig.map(t => (
          <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: t.bg, border: `1px solid ${t.color}` }} />
            <span>{t.label}: {buckets[t.key].length}</span>
          </div>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="bb-com-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Total Hours</th>
              <th>Milestones Earned</th>
              <th>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((v, i) => {
              const milestones = getEarnedMilestones(v);
              return (
                <tr key={v.id}>
                  <td style={{ color: '#9CA3AF', fontSize: 12 }}>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{v.displayName}</td>
                  <td>{v.email || '—'}</td>
                  <td style={{ fontWeight: 700, color: '#4338CA' }}>{(v.totalHours || 0).toFixed(1)} h</td>
                  <td>
                    {milestones.length
                      ? milestones.map(m => (
                          <span key={m.key} className="bb-com-milestone">{m.icon} {m.label}</span>
                        ))
                      : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                  </td>
                  <td>{fmtDate(v.updatedAt)}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No volunteers.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Low Activity Tab ───────────────────────────────────────────────────────

function LowActivityVolunteerTab({ volunteers }) {
  const lowActivity = volunteers.filter(v => (v.totalHours || 0) < 10);

  const getDaysSince = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.round((Date.now() - d.getTime()) / 86400000);
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 16 }}>
        {lowActivity.length} volunteer(s) with fewer than 10 hours logged
      </div>

      {lowActivity.length === 0 ? (
        <div className="bb-empty-state" style={{ color: '#065F46', background: '#D1FAE5', borderRadius: 10, padding: 32 }}>
          No low-activity volunteers — great engagement!
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="bb-com-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Total Hours</th>
                <th>Last Active</th>
                <th>Days Since Active</th>
              </tr>
            </thead>
            <tbody>
              {lowActivity.map(v => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 600 }}>{v.displayName}</td>
                  <td>{v.email || '—'}</td>
                  <td style={{ fontWeight: 700, color: '#991B1B' }}>{(v.totalHours || 0).toFixed(1)} h</td>
                  <td>{fmtDate(v.updatedAt)}</td>
                  <td>
                    {(() => {
                      const days = getDaysSince(v.updatedAt);
                      const num  = typeof days === 'number' ? days : null;
                      return (
                        <span style={{ fontWeight: 600, color: num && num > 60 ? '#991B1B' : '#92400E' }}>
                          {days}{typeof days === 'number' ? ' days' : ''}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pending Role Approvals Tab ─────────────────────────────────────────────

function PendingRoleApprovalsTab({ orgId, adminId, onRefresh }) {
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [acting, setActing]       = useState({});
  const [rejectOpen, setRejectOpen] = useState({});
  const [rejectReason, setRejectReason] = useState({});
  const [msg, setMsg] = useState('');

  const ROLE_COLORS = { Facilitator: '#0D9488', Mentor: '#4338CA', Befriender: '#F97316' };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await getPendingVolunteerRoles(orgId);
      setRequests(rows.filter(r => r.status === 'pending'));
    } catch (e) {
      setError(e.message || 'Failed to load pending role requests.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req) => {
    setActing(a => ({ ...a, [req.id]: 'approving' }));
    setMsg('');
    try {
      await approveVolunteerRole(orgId, req.id, 'approved', adminId);
      setMsg(`${req.volunteerName}'s ${req.type} application has been approved. They have been notified.`);
      await load();
      onRefresh();
    } catch (e) {
      setMsg(e.message || 'Failed to approve.');
    } finally {
      setActing(a => ({ ...a, [req.id]: null }));
    }
  };

  const handleReject = async (req) => {
    const reason = (rejectReason[req.id] || '').trim();
    if (!reason) { setMsg('Please provide a reason before rejecting.'); return; }
    setActing(a => ({ ...a, [req.id]: 'rejecting' }));
    setMsg('');
    try {
      await approveVolunteerRole(orgId, req.id, 'rejected', adminId, reason);
      setMsg(`${req.volunteerName}'s ${req.type} application has been rejected. They have been notified.`);
      setRejectOpen(o => ({ ...o, [req.id]: false }));
      setRejectReason(r => ({ ...r, [req.id]: '' }));
      await load();
      onRefresh();
    } catch (e) {
      setMsg(e.message || 'Failed to reject.');
    } finally {
      setActing(a => ({ ...a, [req.id]: null }));
    }
  };

  const fmtTs = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="bb-loading-row">Loading pending role requests…</div>;

  return (
    <div>
      {msg && (
        <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>{msg}</div>
      )}
      {error && (
        <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{error}</div>
      )}
      {requests.length === 0 ? (
        <div className="bb-empty-state">No pending role approval requests.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {requests.map(req => {
            const color = ROLE_COLORS[req.type] || '#6B7280';
            const isActing = !!acting[req.id];
            const showReject = rejectOpen[req.id];
            return (
              <div key={req.id} className="bb-card" style={{ padding: 0, overflow: 'hidden', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                <div style={{ background: color, height: 5 }} />
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>{req.volunteerName}</div>
                      <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ background: color, color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, padding: '2px 10px' }}>
                          {req.type}
                        </span>
                        <span style={{ fontSize: 12, color: '#9CA3AF' }}>Requested {fmtTs(req.createdAt)}</span>
                      </div>
                    </div>
                    <span className="bb-com-status-pending">Pending</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                        Bio / Why this role
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                        {req.bio || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not provided</span>}
                      </div>
                    </div>
                    <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                        Experience Summary
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                        {req.experience || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not provided</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Initial Availability: </span>
                    <span style={{ fontSize: 12, color: req.availability === 'active' ? '#059669' : '#D97706', fontWeight: 700 }}>
                      {req.availability === 'active' ? 'Active — available now' : 'Unavailable — not ready yet'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <button
                      className="bb-btn-primary"
                      style={{ background: '#16A34A', fontSize: 13, padding: '7px 18px' }}
                      disabled={isActing}
                      onClick={() => handleApprove(req)}
                    >
                      {acting[req.id] === 'approving' ? 'Approving…' : '✓ Approve'}
                    </button>

                    {!showReject ? (
                      <button
                        className="bb-btn-danger-sm"
                        style={{ fontSize: 13, padding: '7px 18px' }}
                        disabled={isActing}
                        onClick={() => setRejectOpen(o => ({ ...o, [req.id]: true }))}
                      >
                        ✕ Reject
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flex: 1, minWidth: 280 }}>
                        <textarea
                          className="bb-textarea"
                          rows={2}
                          style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}
                          placeholder="Reason for rejection (required)…"
                          value={rejectReason[req.id] || ''}
                          onChange={e => setRejectReason(r => ({ ...r, [req.id]: e.target.value }))}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button
                            className="bb-btn-danger-sm"
                            style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}
                            disabled={isActing}
                            onClick={() => handleReject(req)}
                          >
                            {acting[req.id] === 'rejecting' ? 'Rejecting…' : 'Confirm Reject'}
                          </button>
                          <button
                            className="bb-btn-outline-sm"
                            style={{ fontSize: 11 }}
                            onClick={() => {
                              setRejectOpen(o => ({ ...o, [req.id]: false }));
                              setRejectReason(r => ({ ...r, [req.id]: '' }));
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function VolunteerProfiles({ orgId, adminId, adminName }) {
  const [activeTab, setActiveTab] = useState('all');
  const [volunteers, setVolunteers] = useState([]);
  const [pendingRoleCount, setPendingRoleCount] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [data, roleReqs] = await Promise.all([
        getVolunteers(orgId),
        getPendingVolunteerRoles(orgId).catch(() => []),
      ]);
      setVolunteers(data);
      setPendingRoleCount(roleReqs.filter(r => r.status === 'pending').length);
    } catch (e) {
      setError(e.message || 'Failed to load volunteers.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'all',          label: 'All Volunteers' },
    { key: 'role_pending', label: `Pending Role Approvals${pendingRoleCount > 0 ? ` (${pendingRoleCount})` : ''}` },
    { key: 'engagement',   label: 'Engagement Dashboard' },
    { key: 'low',          label: `Low Activity (${volunteers.filter(v => (v.totalHours || 0) < 10).length})` },
  ];

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2 className="bb-prog-tool-title">Volunteer Profiles</h2>
        <p className="bb-prog-tool-desc">Manage volunteers, approvals, and hour tracking.</p>
        <div className="bb-prog-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
              style={t.key === 'role_pending' && pendingRoleCount > 0 ? { position: 'relative' } : {}}
            >
              {t.label}
              {t.key === 'role_pending' && pendingRoleCount > 0 && (
                <span style={{
                  background: '#EF4444', color: '#fff',
                  borderRadius: 10, fontSize: 10, fontWeight: 700,
                  padding: '1px 6px', marginLeft: 6,
                }}>
                  {pendingRoleCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bb-prog-tab-content">
        {error && <div className="bb-alert bb-alert--error">{error}</div>}
        {loading ? (
          <div className="bb-loading-row">Loading volunteers…</div>
        ) : (
          <>
            {activeTab === 'all' && (
              <VolunteerTable volunteers={volunteers} orgId={orgId} adminId={adminId} onRefresh={load} showCreateBtn />
            )}
            {activeTab === 'role_pending' && (
              <PendingRoleApprovalsTab orgId={orgId} adminId={adminId} onRefresh={load} />
            )}
            {activeTab === 'engagement' && (
              <VolunteerEngagementDashboardTab volunteers={volunteers} />
            )}
            {activeTab === 'low' && (
              <LowActivityVolunteerTab volunteers={volunteers} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
